
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { CustomerInfoForm } from "@/components/permits/CustomerInfoForm";
import { TagIcon, ShoppingBag, Target } from "lucide-react";

export interface BookingFormProps {
  selectedHunt: string | null;
  selectedPackage: string | null;
  huntTypes: any[];
  packageTypes: any[];
  customerName: string;
  setCustomerName: (value: string) => void;
  customerEmail: string;
  setCustomerEmail: (value: string) => void;
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  address: string;
  setAddress: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  state: string;
  setState: (value: string) => void;
  zipCode: string;
  setZipCode: (value: string) => void;
  huntingLicense: string;
  setHuntingLicense: (value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  setShowBookingForm: (show: boolean) => void;
}

export const BookingForm: React.FC<BookingFormProps> = ({
  selectedHunt,
  selectedPackage,
  huntTypes,
  packageTypes,
  customerName,
  setCustomerName,
  customerEmail,
  setCustomerEmail,
  phoneNumber,
  setPhoneNumber,
  address,
  setAddress,
  city,
  setCity,
  state,
  setState,
  zipCode,
  setZipCode,
  huntingLicense,
  setHuntingLicense,
  handleSubmit,
  setShowBookingForm,
}) => {
  const selectedPackageData = packageTypes.find(p => p.id === selectedPackage);
  const totalPrice = selectedPackageData?.price || 0;

  return (
    <div id="booking-form" className="mt-12 bg-gray-900 p-6 md:p-8 rounded-lg shadow-md max-w-7xl mx-auto mb-12">
      <h2 className="text-2xl font-bold mb-6 text-white flex items-center">
        <TagIcon className="mr-2 h-6 w-6 text-tribal-primary" />
        Reserve Your Guided Hunt
      </h2>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-8 bg-black/30 p-4 rounded-md">
          {selectedHunt && selectedPackage && (
            <div className="space-y-2">
              <h3 className="font-semibold text-lg text-tribal-primary">Selected Hunt</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Hunt Type:</p>
                  <p className="font-medium">{huntTypes.find(h => h.id === selectedHunt)?.title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Package:</p>
                  <p className="font-medium">{packageTypes.find(p => p.id === selectedPackage)?.title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Season:</p>
                  <p className="font-medium">{huntTypes.find(h => h.id === selectedHunt)?.season}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Price:</p>
                  <p className="font-medium text-amber-500">
                    {totalPrice.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <CustomerInfoForm 
          customerName={customerName} 
          setCustomerName={setCustomerName} 
          customerEmail={customerEmail} 
          setCustomerEmail={setCustomerEmail} 
          phoneNumber={phoneNumber} 
          setPhoneNumber={setPhoneNumber} 
          address={address} 
          setAddress={setAddress} 
          city={city} 
          setCity={setCity} 
          state={state} 
          setState={setState} 
          zipCode={zipCode} 
          setZipCode={setZipCode} 
        />
        
        <div className="mb-8">
          <h3 className="font-semibold text-lg text-white mb-4">Hunting Information</h3>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="hunting-license" className="flex items-center text-gray-300">
                <Target className="w-4 h-4 mr-2" />
                State Hunting License Number
              </Label>
              <Input 
                id="hunting-license" 
                value={huntingLicense} 
                onChange={e => setHuntingLicense(e.target.value)} 
                placeholder="Enter your hunting license number" 
                className="bg-gray-800 border-gray-700 text-white" 
              />
              <p className="text-xs text-gray-400 mt-1">
                Optional: Your state hunting license number for reference
              </p>
            </div>
          </div>
        </div>
        
        <div className="mb-8">
          <h3 className="font-semibold text-lg text-white mb-4">Hunt Summary</h3>
          
          <div className="bg-gray-800 p-4 rounded-md">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>
                  {selectedHunt && selectedPackage ? 
                    `${huntTypes.find(h => h.id === selectedHunt)?.title} - ${packageTypes.find(p => p.id === selectedPackage)?.title}` 
                    : 'Selected Hunt Package'}
                </span>
                <span>
                  {totalPrice.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD'
                  })}
                </span>
              </div>
              
              <Separator className="my-2 bg-gray-700" />
              
              <div className="flex justify-between font-bold text-base">
                <span>Total</span>
                <span className="text-amber-500">
                  {totalPrice.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD'
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-8 p-4 bg-amber-900/30 border border-amber-900/50 rounded-md text-sm">
          <p className="mb-4">
            <strong>Important Notes:</strong>
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-300">
            <li>Your guided hunt reservation is for the selected season.</li>
            <li>The exact hunt dates within this period will be coordinated after booking.</li>
            <li>This reservation secures your tribal tag and guided hunt experience.</li>
            <li>A 50% deposit is required at checkout, with the balance due 60 days before your hunt.</li>
            <li>Cancellations less than 90 days before hunt date are non-refundable.</li>
          </ul>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 justify-end">
          <Button 
            type="button" 
            variant="outline" 
            className="text-gray-300 border-gray-700 hover:bg-gray-800" 
            onClick={() => setShowBookingForm(false)}
          >
            Cancel
          </Button>
          <Button type="submit" className="bg-tribal-primary hover:bg-tribal-primary/90">
            <ShoppingBag className="mr-2 h-4 w-4" />
            Add to Cart
          </Button>
        </div>
      </form>
    </div>
  );
};
